import { ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useReactHookForm } from '../hooks/useReactHookForm';
import { ReactHookForm } from './hook-form/react-hook-form.component';
import { ApiErrorComponent } from './api-error.component';
import { LoadingIndicator } from './loading-indicator.component';
import { useSaveData } from '../hooks/useSaveData';
import { MutationKeyConfig } from '../api/api-helper';
import { InvalidateRoute } from '../hooks/useDeleteData';
import { UserPermissionName } from '../models/user-permission-name';
import { useAuthorize } from '../hooks/use-authorize';
import { useSnackbar } from '../hooks/use-snackbar.hook';

interface SaveDataDialogProps {
  children: ReactNode;
  title: any;
  config: { saveConfig: MutationKeyConfig; invalidateRoute?: InvalidateRoute };
  data: any;
  onClose: () => void;
  onShowWarning?: (data: any) => void;
  onSaveCompleted?: (data: any) => void;
  yupSchema?: any;
  formatSubmitdata?: (data: any) => any;
  size: any;
  isLoading?: boolean;
}

export const SaveHookFormDialog = ({
  children,
  title,
  data,
  yupSchema,
  config: { saveConfig, invalidateRoute },
  onClose,
  onShowWarning,
  onSaveCompleted,
  formatSubmitdata,
  size,
  isLoading: isLoadingFromApi,
}: SaveDataDialogProps) => {
  const form = useReactHookForm({
    defaultValues: {
      ...data,
    },
    yupSchema,
  });

  const { showSnackbar } = useSnackbar();

  const { isLoading, isError, error, mutateAsync } = useSaveData(saveConfig, invalidateRoute);

  const closeDialogHandler = () => {
    form.reset();
    onClose();
  };

  const submitHanlder = async (data: any) => {
    const formattedData = formatSubmitdata ? formatSubmitdata(data) : data;
    if (formattedData) {
      onShowWarning?.(formattedData);
      const result = await mutateAsync({ data: formattedData });
      onSaveCompleted?.(result.data);
      closeDialogHandler();
    }
  };

  const checkUnitAddressErrors = (data: any): boolean => {
    // ✅ Empty object
    if (!data || Object.keys(data)?.length === 0) {
      return false;
    }

    const unit = data?.unitData;

    if (!unit) return true;

    // Check if only address exists
    const onlyAddressType =
      Object.keys(unit)?.length === 1 && unit?.address;

    // Check if message contains "not allowed"
    const isNotAllowed =
      unit?.address?.message
        ?.toLowerCase()
        ?.includes("not allowed");

    if (onlyAddressType && isNotAllowed) {
      return false;
    }

    return true;
  };

  const handleSubmitClick = () => {
    // if (checkUnitAddressErrors(form?.formState?.errors)) {
    //   showSnackbar("Please fix duplicate value errors before submitting.", "error");
    //   return;
    // }
    form.handleSubmit(submitHanlder)();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog open={true} fullWidth={true} maxWidth={size}>
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={closeDialogHandler}
        sx={{
          position: 'absolute',
          right: 10,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <ReactHookForm formName="SEVE_FORM" onSubmit={submitHanlder} formContextProps={form}>
        <DialogContent dividers className="popupDilog">
          <LoadingIndicator isLoading={isLoading} isSpinner />
          {/* <ApiErrorComponent isError={isError} errorRespnse={error} /> */}
          {children}
        </DialogContent>
        <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
          <DialogActions className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={closeDialogHandler}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isLoading || isLoadingFromApi || isReadOnlyPermission}
              variant="contained"
              style={{ width: '130px' }}
              onClick={handleSubmitClick}
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </ReactHookForm>
    </Dialog>
  );
};
